import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'
import { i18nGlobal } from '../i18n'
import type { Section } from '../types'

/**
 * Navigatieboom van de documentatie.
 *
 * De boom komt kant-en-klaar genest van de API:
 * - beheer:   GET /sections (alles, incl. verborgen en concepten)
 * - bezoeker: GET /navigation (alleen zichtbaar + gepubliceerd, geen auth)
 *
 * Mutaties sturen snake_case payloads (de API verwacht dat; alleen
 * responses worden naar camelCase getransformeerd) en verversen daarna
 * de boom zodat alle volgordes/tellingen kloppen.
 */
export const useSectionStore = defineStore('sections', () => {
  const tree = ref<Section[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const loadedForVisitor = ref<boolean | null>(null)

  async function fetchTree(asVisitor: boolean) {
    isLoading.value = true
    error.value = null

    try {
      if (asVisitor) {
        const response = await api.get('/navigation')
        tree.value = response.data.navigation
      } else {
        const response = await api.get('/sections', { params: { tree: 1 } })
        tree.value = response.data.data
      }
      loadedForVisitor.value = asVisitor
    } catch (err: any) {
      error.value = err.response?.data?.message || i18nGlobal.t('chrome.sidebar.loadFailed')
    } finally {
      isLoading.value = false
    }
  }

  async function refresh() {
    if (loadedForVisitor.value !== null) {
      await fetchTree(loadedForVisitor.value)
    }
  }

  /* ── Secties (categorieën & subcategorieën) ────────────────── */

  async function createSection(data: { name: string; parentId?: number | null; sortOrder?: number }) {
    await api.post('/sections', {
      name: data.name,
      parent_id: data.parentId ?? null,
      // Zonder expliciete sort_order default de backend naar 0, waardoor een
      // nieuw item tussen bestaande belandt; nieuw = achteraan.
      sort_order: data.sortOrder ?? undefined,
    })
    await refresh()
  }

  async function updateSection(
    id: number,
    data: { name?: string; isVisible?: boolean; parentId?: number | null; sortOrder?: number }
  ) {
    const payload: Record<string, unknown> = {}
    if (data.name !== undefined) payload.name = data.name
    if (data.isVisible !== undefined) payload.is_visible = data.isVisible
    if (data.parentId !== undefined) payload.parent_id = data.parentId
    if (data.sortOrder !== undefined) payload.sort_order = data.sortOrder

    await api.put(`/sections/${id}`, payload)
    await refresh()
  }

  async function deleteSection(id: number) {
    await api.delete(`/sections/${id}`)
    await refresh()
  }

  /**
   * Slot van één categorie (Starter-functie). `mode: 'inherit'` haalt het
   * slot weg: de categorie volgt dan weer zijn ouder, uiteindelijk de site.
   * Een leeg wachtwoord laat een bestaand wachtwoord ongemoeid.
   */
  async function updateSectionAccess(
    id: number,
    data: {
      mode: 'inherit' | 'public' | 'password' | 'team'
      password?: string | null
      hint?: string | null
      visibility?: 'locked' | 'hidden'
    }
  ) {
    await api.put(`/sections/${id}/access`, {
      mode: data.mode,
      password: data.password || null,
      hint: data.hint || null,
      visibility: data.visibility ?? 'locked',
    })
    await refresh()
  }

  async function reorderSections(items: Array<{ id: number; sortOrder: number; parentId: number | null }>) {
    await api.post('/sections/reorder', {
      sections: items.map(i => ({ id: i.id, sort_order: i.sortOrder, parent_id: i.parentId })),
    })
    await refresh()
  }

  /* ── Pagina's ──────────────────────────────────────────────── */

  /** Geeft de aangemaakte pagina terug, zodat de aanroeper er meteen
   *  naartoe kan navigeren (de editor openen op een verse pagina). */
  async function createPage(data: { title: string; sectionId: number; parentId?: number | null }) {
    const response = await api.post('/pages', {
      title: data.title,
      section_id: data.sectionId,
      parent_id: data.parentId ?? null,
      status: 'draft',
    })
    await refresh()
    return response.data?.page ?? response.data?.data ?? null
  }

  async function updatePage(
    id: number,
    data: { title?: string; isVisible?: boolean; sectionId?: number; sortOrder?: number }
  ) {
    const payload: Record<string, unknown> = {}
    if (data.title !== undefined) payload.title = data.title
    if (data.isVisible !== undefined) payload.is_visible = data.isVisible
    if (data.sectionId !== undefined) payload.section_id = data.sectionId
    if (data.sortOrder !== undefined) payload.sort_order = data.sortOrder

    await api.put(`/pages/${id}`, payload)
    await refresh()
  }

  async function deletePage(id: number) {
    await api.delete(`/pages/${id}`)
    await refresh()
  }

  async function duplicatePage(id: number) {
    await api.post(`/pages/${id}/duplicate`)
    await refresh()
  }

  async function reorderPages(items: Array<{ id: number; sortOrder: number; sectionId?: number }>) {
    await api.post('/pages/reorder', {
      pages: items.map(i => ({ id: i.id, sort_order: i.sortOrder, section_id: i.sectionId ?? null })),
    })
    await refresh()
  }

  /* ── Actieve hoofdcategorie (header ↔ sidebar-scope) ───────── */

  const activeRootId = ref<number | null>(
    Number(localStorage.getItem('active_root_category')) || null
  )

  function setActiveRoot(id: number | null) {
    activeRootId.value = id
    if (id === null) localStorage.removeItem('active_root_category')
    else localStorage.setItem('active_root_category', String(id))
  }

  /** Wandel van een sectie omhoog naar zijn hoofdcategorie. */
  function rootOf(sectionId: number): Section | null {
    const flat = sections.value
    let current = flat.find(s => s.id === sectionId) ?? null
    while (current && current.parentId !== null) {
      current = flat.find(s => s.id === current!.parentId) ?? null
    }
    return current
  }

  /** Effectieve actieve hoofdcategorie. Valt terug op de eerste wanneer het
   *  bewaarde id niet (meer) in de boom zit — verwijderd, of voor bezoekers
   *  weggefilterd omdat de categorie verborgen is. */
  const activeRoot = computed<Section | null>(() =>
    tree.value.find(r => r.id === activeRootId.value) ?? tree.value[0] ?? null
  )

  /* ── Boom-helpers ──────────────────────────────────────────── */

  function findSection(id: number, list?: Section[]): Section | null {
    for (const section of list ?? tree.value) {
      if (section.id === id) return section
      if (section.children?.length) {
        const found = findSection(id, section.children)
        if (found) return found
      }
    }
    return null
  }

  /** Alle categorieën plat, met diepte (voor "verplaats naar"-doelen). */
  function allCategories(): Array<{ section: Section; depth: number }> {
    const result: Array<{ section: Section; depth: number }> = []
    for (const root of tree.value) {
      result.push({ section: root, depth: 0 })
      for (const child of root.children ?? []) {
        result.push({ section: child, depth: 1 })
      }
    }
    return result
  }

  /* ── Compatibiliteit met bestaande views ───────────────────── */

  /** Vlakke lijst van alle secties (BreadcrumbNav, PageView, PagesView). */
  const sections = computed<Section[]>(() => {
    const flat: Section[] = []
    for (const root of tree.value) {
      flat.push(root)
      for (const child of root.children ?? []) flat.push(child)
    }
    return flat
  })

  async function fetchSections() {
    await fetchTree(false)
  }

  /**
   * Het publieke adres van een pagina, of null als die er niet is.
   *
   * Een pagina heeft twee adressen: /pages/:id (intern, vereist inloggen) en
   * /docs/categorie/pagina (publiek). Alleen een gepubliceerde, zichtbare
   * pagina heeft het tweede - een concept of verborgen pagina levert null,
   * zodat de aanroeper weet dat er niets te delen valt.
   *
   * Tabs krijgen het adres met drie segmenten, net als in de navigatie.
   */
  function publicPathForPage(pageId: number): string | null {
    const deelbaar = (p: { status?: string; isVisible?: boolean }) =>
      p.status === 'published' && p.isVisible !== false

    const zoek = (secties: Section[]): string | null => {
      for (const sectie of secties) {
        for (const pagina of sectie.pages ?? []) {
          if (pagina.id === pageId) {
            return deelbaar(pagina) ? `/docs/${sectie.slug}/${pagina.slug}` : null
          }

          // Tabs hangen onder hun ouderpagina; die moet zelf ook deelbaar zijn,
          // want het adres van een tab bevat de ouder.
          for (const tab of pagina.children ?? []) {
            if (tab.id === pageId) {
              return deelbaar(tab) && deelbaar(pagina)
                ? `/docs/${sectie.slug}/${pagina.slug}/${tab.slug}`
                : null
            }
          }
        }

        const uitSubcategorie = zoek(sectie.children ?? [])
        if (uitSubcategorie !== null) return uitSubcategorie
      }

      return null
    }

    return zoek(tree.value)
  }

  return {
    tree,
    publicPathForPage,
    sections,
    isLoading,
    error,
    fetchTree,
    fetchSections,
    refresh,
    createSection,
    updateSection,
    updateSectionAccess,
    deleteSection,
    reorderSections,
    createPage,
    updatePage,
    deletePage,
    duplicatePage,
    reorderPages,
    findSection,
    allCategories,
    activeRootId,
    activeRoot,
    setActiveRoot,
    rootOf,
  }
})
